import React, { useEffect, useMemo } from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import { useTranslation } from 'react-i18next';
import Select from 'components/@common/Inputs/Select';
import useStore from 'store';
import { useField } from 'formik';
import { ENTITY_TYPE_OPTIONS } from '../../../utils/report-param-utils';
import { ReportProduct } from 'components/ReportsModule/types';
import { Organization, OrganizationType } from 'types/schema-generated-types';
import GET_REPORTING_ORGANIZATIONS from 'graph/organization/get-reporting-organizations-query';
import { useQuery } from '@apollo/client';
import { uniq } from 'lodash';

type Props = {
  name?: string;
  productFamilyCode: ReportProduct;
} & React.ComponentProps<typeof Select>;

const EntityTypeField: React.FC<Props> = ({ name = 'entityType', productFamilyCode, ...props }) => {
  const { t } = useTranslation('Reports');
  const [field, meta, { setValue }] = useField(name);
  const entityType = field.value;

  const entityTypeOptions = useEntityTypeOptions(productFamilyCode);

  useEffect(() => {
    if (entityTypeOptions.length && !entityTypeOptions.some(option => option.type === entityType)) {
      setValue(entityTypeOptions[0].type);
    }
  }, [entityTypeOptions, entityType, setValue]);

  return (
    <Select
      label={t('Select Report Level')}
      placeholder={t('Select Report Level')}
      size="small"
      inputProps={{
        'aria-label': t('Select Report Level')
      }}
      {...field}
      {...props}
      error={!!meta.error && meta.touched}
    >
      {entityTypeOptions.map(option => (
        <MenuItem key={`${option.type.valueOf()}-reportlevel-option`} value={option.type.valueOf()}>
          {t(option.type)}
        </MenuItem>
      ))}
    </Select>
  );
};

const useEntityTypeOptions = (productFamilyCode: ReportProduct) => {
  const [{ userPrivilegesCache }] = useStore();
  const { data } = useQuery(GET_REPORTING_ORGANIZATIONS);

  const entityTypeOptions = useMemo(() => {
    if (!data?.organizations) return [];

    const getOrganizationTypes = (organizationIds: number[]) => {
      const organizations: Organization[] = data.organizations;
      const organizationTypes = organizations.reduce((acc: OrganizationType[], organization) => {
        if (!organizationIds.includes(organization.id)) return acc;
        return [...acc, organization.type];
      }, []);
      return uniq(organizationTypes);
    };

    return ENTITY_TYPE_OPTIONS.filter(({ organizationType, requiredOrgPrivilege }) => {
      if (!requiredOrgPrivilege?.[productFamilyCode]) return false;
      if (!organizationType) return true;
      const privilegedOrgIds = userPrivilegesCache.getPrivilegedOrgIds(
        `${requiredOrgPrivilege[productFamilyCode]}`
      );
      return getOrganizationTypes(privilegedOrgIds).includes(organizationType);
    });
  }, [userPrivilegesCache, productFamilyCode, data]);

  return entityTypeOptions;
};

export default EntityTypeField;
